import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { ChevronDown, Image as ImageIcon, RefreshCw, Save, Trash2, Upload } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { SettingsLayout } from '../components/settings-layout'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select } from '../components/ui/select'
import { api, type InvoiceSettings } from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/invoice-layout',
  component: InvoiceLayoutSettings,
})

type Patch = Partial<Omit<InvoiceSettings, 'id' | 'updatedAt'>>

// === Template-Vorgaben ===
// Each preset is a snapshot of layout-shaping fields. Selecting a template
// merges these on top of the current form so the user can still tweak any
// individual field afterwards.
const TEMPLATE_PRESETS: Array<{
  id: InvoiceSettings['layoutTemplate']
  label: string
  hint: string
  patch: Partial<InvoiceSettings>
}> = [
  {
    id: 'professional',
    label: 'Professional',
    hint: 'Formeller Briefkopf mit Logo oben rechts und 7-Spalten-Positionstabelle (inkl. Art.Nr.).',
    patch: {
      layoutTemplate: 'professional',
      headerStyle: 'professional',
      headerMetaLayout: 'split-lr',
      tableHeaderStyle: 'minimal',
      tableZebra: false,
      totalsBoxStyle: 'plain',
      marginPreset: 'normal',
      fontFamily: 'helvetica',
      receiverLabel: '',
      showArticleNumber: true,
      subtitleNetto: true,
      currencyDisplay: 'symbol',
      totalsLabelTotal: 'Endsumme',
      totalsLabelSubtotal: 'Zwischensumme',
      logoPosition: 'right',
      logoMaxHeight: 120,
      pageNumbers: false,
      accentColor: '#000000',
    },
  },
]

function InvoiceLayoutSettings() {
  const qc = useQueryClient()
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data, isLoading } = useQuery({
    queryKey: ['invoice-settings'],
    queryFn: () => api.getInvoiceSettings(),
    enabled: !!meData?.user,
  })

  const [form, setForm] = useState<InvoiceSettings | null>(null)
  useEffect(() => {
    if (data?.settings) setForm(data.settings)
  }, [data])

  // Live-preview state — debounced blob URL that re-renders the sample
  // invoice whenever the form changes. Old blob URLs are revoked on swap to
  // avoid leaking memory in long sessions.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!form) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        setPreviewLoading(true)
        const { id: _id, updatedAt: _u, ...override } = form
        const url = await api.invoicePreviewLayoutBlob(override)
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev)
          return url
        })
      } catch (e) {
        notify.error((e as Error).message)
      } finally {
        setPreviewLoading(false)
      }
    }, 500)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [form])
  // Revoke last blob URL on unmount.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const mutation = useMutation({
    mutationFn: (patch: Patch) => api.updateInvoiceSettings(patch),
    onSuccess: (r) => {
      qc.setQueryData(['invoice-settings'], { settings: r.settings })
      notify.success('Rechnungs-Layout gespeichert')
    },
    onError: (e: Error) => notify.error(e.message),
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (meData.user.role !== 'admin') return <Navigate to="/" />
  if (isLoading || !form) return <SettingsLayout title="Rechnungs-Layout">Lade …</SettingsLayout>

  function setField<K extends keyof InvoiceSettings>(key: K, value: InvoiceSettings[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f))
  }

  function applyPreset(presetId: InvoiceSettings['layoutTemplate']) {
    const preset = TEMPLATE_PRESETS.find((p) => p.id === presetId)
    if (!preset) return
    setForm((f) => (f ? { ...f, ...preset.patch } : f))
  }

  function save() {
    if (!form) return
    const { id: _id, updatedAt: _upd, ...patch } = form
    mutation.mutate({
      ...patch,
      defaultTaxRatePercent: Number(form.defaultTaxRatePercent) as unknown as string,
      watermarkOpacity: Number(form.watermarkOpacity) as unknown as string,
    })
  }

  async function uploadImage(field: 'logoDataUrl' | 'signatureDataUrl', file: File) {
    if (!file.type.match(/^image\/(png|jpe?g)$/)) {
      notify.error('Nur PNG oder JPEG erlaubt')
      return
    }
    if (file.size > 1024 * 1024) {
      notify.error('Datei zu groß (max. 1 MB)')
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = String(reader.result || '')
      setField(field, dataUrl)
    }
    reader.readAsDataURL(file)
  }

  return (
    <SettingsLayout
      title="Rechnungs-Layout-Designer"
      description="Aussteller, Bank, Defaults und Layout. Die Vorschau rendert eine Beispielrechnung live mit deinen ungesicherten Änderungen — speichern erst nach Klick auf »Speichern«."
    >
      <div className="space-y-4">
        {/* Top block: Vorschau links + Layout-Sektionen rechts (klappbar) */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
          {/* Live-Vorschau links — sticky, damit sie beim Scrollen sichtbar bleibt */}
          <div className="xl:sticky xl:top-20">
            <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
              <header className="px-4 py-2 border-b border-border-subtle bg-bg-overlay/30 flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-text-tertiary">
                  Live-Vorschau (Beispielrechnung)
                </span>
                {previewLoading && (
                  <span className="inline-flex items-center gap-1 text-xs text-text-tertiary">
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    rendere …
                  </span>
                )}
              </header>
              {/* iframe <640: feste 600px statt der 100vh-Rechnung (bezog
                  die mitscrollende Topbar ein — nach dem Shell-Umbau falsche
                  Referenz; die Seite scrollt im Shell-Scroller, nichts liegt
                  hinter der Bottom-Bar). ≥640 byte-identisch. */}
              {previewUrl ? (
                <iframe
                  src={previewUrl}
                  title="Rechnungs-Layout-Vorschau"
                  className="w-full h-[600px] sm:h-[calc(100vh-160px)] sm:min-h-[600px] bg-white"
                />
              ) : (
                <div className="flex items-center justify-center h-[600px] text-sm text-text-tertiary">
                  Vorschau wird geladen …
                </div>
              )}
            </section>
          </div>

          {/* Layout-Sektionen rechts — alle klappbar, damit sie neben die
              Vorschau passen ohne Scroll-Marathon. */}
          <div className="space-y-3">
            {/* Vorlage */}
            <Section title="Vorlage" collapsible defaultOpen>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {TEMPLATE_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => applyPreset(p.id)}
                    className={cn(
                      'text-left rounded-xl border p-3 transition-colors',
                      form.layoutTemplate === p.id
                        ? 'border-accent bg-accent/10 ring-1 ring-accent/40'
                        : 'border-border-subtle bg-bg-overlay/40 hover:border-border-default',
                    )}
                  >
                    <div className="text-sm font-medium text-text-primary">{p.label}</div>
                    <div className="text-xs text-text-tertiary mt-0.5">{p.hint}</div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-text-tertiary">
                Die Vorlage setzt mehrere Felder gleichzeitig; einzelne Werte kannst du danach
                jederzeit überschreiben.
              </p>
            </Section>

            {/* Typografie & Maße */}
            <Section title="Typografie & Seitenränder" collapsible>
              <Field label="Schrift">
                <Select
                  value={form.fontFamily}
                  onChange={(e) =>
                    setField('fontFamily', e.target.value as InvoiceSettings['fontFamily'])
                  }
                >
                  <option value="helvetica">Helvetica (sans-serif)</option>
                  <option value="times">Times Roman (serif)</option>
                  <option value="courier">Courier (mono)</option>
                </Select>
              </Field>
              <Field label="Seitenränder">
                <Select
                  value={form.marginPreset}
                  onChange={(e) =>
                    setField('marginPreset', e.target.value as InvoiceSettings['marginPreset'])
                  }
                >
                  <option value="compact">Kompakt</option>
                  <option value="normal">Normal</option>
                  <option value="generous">Großzügig</option>
                </Select>
              </Field>
            </Section>

            {/* Header & Logo */}
            <Section title="Header & Logo" collapsible>
              <Field label="Header-Stil">
                <Select
                  value={form.headerStyle}
                  onChange={(e) =>
                    setField('headerStyle', e.target.value as InvoiceSettings['headerStyle'])
                  }
                >
                  <option value="plain">Schlicht (Linie)</option>
                  <option value="band">Vollflächiges Akzent-Band</option>
                  <option value="sidebar">Akzent-Sidebar</option>
                  <option value="boxed">Box mit Rahmen</option>
                  <option value="professional">Professional (nur Logo)</option>
                </Select>
              </Field>
              <Two>
                <Field label="Rechnungs-Meta-Layout">
                  <Select
                    value={form.headerMetaLayout}
                    onChange={(e) =>
                      setField(
                        'headerMetaLayout',
                        e.target.value as InvoiceSettings['headerMetaLayout'],
                      )
                    }
                  >
                    <option value="right-block">Rechter Block (RECHNUNG-Titel + Key/Value)</option>
                    <option value="split-lr">Links/rechts geteilt (Nr links, Datum rechts)</option>
                  </Select>
                </Field>
                <Field label="Empfänger-Label (leer = aus)">
                  <Input
                    value={form.receiverLabel}
                    onChange={(e) => setField('receiverLabel', e.target.value)}
                    placeholder="AN"
                  />
                </Field>
              </Two>
              <Two>
                <Field label="Akzentfarbe">
                  <ColorField
                    value={form.accentColor}
                    onChange={(v) => setField('accentColor', v)}
                  />
                </Field>
                <Field label="Header-BG (überschreibt Akzent)">
                  <ColorField
                    value={form.headerBgColor ?? ''}
                    onChange={(v) => setField('headerBgColor', v || null)}
                    allowEmpty
                  />
                </Field>
              </Two>
              <Field label="Header-Textfarbe (auto bei leer)">
                <ColorField
                  value={form.headerTextColor ?? ''}
                  onChange={(v) => setField('headerTextColor', v || null)}
                  allowEmpty
                />
              </Field>
              <Two>
                <Field label="Logo-Position">
                  <Select
                    value={form.logoPosition}
                    onChange={(e) =>
                      setField('logoPosition', e.target.value as InvoiceSettings['logoPosition'])
                    }
                  >
                    <option value="left">Links</option>
                    <option value="right">Rechts</option>
                    <option value="center">Mittig</option>
                    <option value="hidden">Ausblenden</option>
                  </Select>
                </Field>
                <Field label="Logo-Höhe (pt)">
                  <Input
                    type="number"
                    min="20"
                    max="160"
                    value={form.logoMaxHeight}
                    onChange={(e) => setField('logoMaxHeight', Number(e.target.value) || 60)}
                  />
                </Field>
              </Two>
              <ImageField
                label="Logo (PNG/JPG, max 1 MB)"
                value={form.logoDataUrl}
                onChange={(v) => setField('logoDataUrl', v)}
                onUpload={(f) => uploadImage('logoDataUrl', f)}
              />
            </Section>

            {/* Tabelle */}
            <Section title="Positions-Tabelle" collapsible>
              <Field label="Header-Stil">
                <Select
                  value={form.tableHeaderStyle}
                  onChange={(e) =>
                    setField(
                      'tableHeaderStyle',
                      e.target.value as InvoiceSettings['tableHeaderStyle'],
                    )
                  }
                >
                  <option value="filled">Gefüllt (grau)</option>
                  <option value="outlined">Umrandet (Akzent)</option>
                  <option value="minimal">Minimal (nur Text)</option>
                </Select>
              </Field>
              <Toggle
                label="Zebra-Streifen für Zeilen"
                checked={form.tableZebra}
                onChange={(v) => setField('tableZebra', v)}
              />
              <Toggle
                label="Positions-Nummern anzeigen"
                checked={form.showLineNumbers}
                onChange={(v) => setField('showLineNumbers', v)}
              />
              <Toggle
                label="Art.Nr.-Spalte anzeigen"
                checked={form.showArticleNumber}
                onChange={(v) => setField('showArticleNumber', v)}
              />
              <Toggle
                label="USt-Spalte je Zeile anzeigen"
                checked={form.showLineTax}
                onChange={(v) => setField('showLineTax', v)}
              />
              <Toggle
                label="»netto«-Untertitel auf Preis-Spalten"
                checked={form.subtitleNetto}
                onChange={(v) => setField('subtitleNetto', v)}
              />
            </Section>

            {/* Totals & Footer-Layout */}
            <Section title="Summen & Footer" collapsible>
              <Field label="Summen-Box-Stil">
                <Select
                  value={form.totalsBoxStyle}
                  onChange={(e) =>
                    setField('totalsBoxStyle', e.target.value as InvoiceSettings['totalsBoxStyle'])
                  }
                >
                  <option value="plain">Ohne Box</option>
                  <option value="outlined">Umrandet</option>
                  <option value="filled">Gefüllt</option>
                </Select>
              </Field>
              <Two>
                <Field label="Zwischensumme-Bezeichnung">
                  <Input
                    value={form.totalsLabelSubtotal}
                    onChange={(e) => setField('totalsLabelSubtotal', e.target.value)}
                    placeholder="Zwischensumme"
                  />
                </Field>
                <Field label="Gesamt-Bezeichnung">
                  <Input
                    value={form.totalsLabelTotal}
                    onChange={(e) => setField('totalsLabelTotal', e.target.value)}
                    placeholder="Gesamtbetrag / Endsumme"
                  />
                </Field>
              </Two>
              <Field label="Footer-Spalten">
                <Select
                  value={String(form.footerColumns)}
                  onChange={(e) => setField('footerColumns', Number(e.target.value))}
                >
                  <option value="1">1 Spalte</option>
                  <option value="2">2 Spalten</option>
                  <option value="3">3 Spalten (Anschrift / Bank / Steuer)</option>
                </Select>
              </Field>
              <Field label="Footer-Text (zentriert)">
                <Input
                  value={form.footerText ?? ''}
                  onChange={(e) => setField('footerText', e.target.value || null)}
                  placeholder="Vielen Dank für Ihren Auftrag."
                />
              </Field>
              <Toggle
                label="Seitenzahlen anzeigen (ab Seite 2)"
                checked={form.pageNumbers}
                onChange={(v) => setField('pageNumbers', v)}
              />
            </Section>

            {/* Wasserzeichen */}
            <Section title="Wasserzeichen" collapsible>
              <Two>
                <Field label="Text (z.B. ENTWURF)">
                  <Input
                    value={form.watermarkText ?? ''}
                    onChange={(e) => setField('watermarkText', e.target.value || null)}
                    placeholder="ENTWURF"
                  />
                </Field>
                <Field label="Deckkraft (0–1)">
                  <Input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    value={form.watermarkOpacity}
                    onChange={(e) => setField('watermarkOpacity', e.target.value)}
                  />
                </Field>
              </Two>
            </Section>
          </div>
        </div>

        {/* Bottom: Aussteller + Stammdaten in voller Breite, ebenfalls klappbar */}
        <Section title="Aussteller (Pflicht §14 UStG)" collapsible>
          <Field label="Firmenname *">
            <Input
              value={form.issuerName}
              onChange={(e) => setField('issuerName', e.target.value)}
              placeholder="Dennis Paradzinski IT-Dienstleistungen"
            />
          </Field>
          <Two>
            <Field label="Straße + Nr.">
              <Input
                value={form.issuerStreet ?? ''}
                onChange={(e) => setField('issuerStreet', e.target.value || null)}
              />
            </Field>
            <Two>
              <Field label="PLZ">
                <Input
                  value={form.issuerZip ?? ''}
                  onChange={(e) => setField('issuerZip', e.target.value || null)}
                />
              </Field>
              <Field label="Ort">
                <Input
                  value={form.issuerCity ?? ''}
                  onChange={(e) => setField('issuerCity', e.target.value || null)}
                />
              </Field>
            </Two>
          </Two>
          <Two>
            <Field label="E-Mail">
              <Input
                type="email"
                value={form.issuerEmail ?? ''}
                onChange={(e) => setField('issuerEmail', e.target.value || null)}
              />
            </Field>
            <Field label="Telefon">
              <Input
                value={form.issuerPhone ?? ''}
                onChange={(e) => setField('issuerPhone', e.target.value || null)}
              />
            </Field>
          </Two>
          <Field label="Website">
            <Input
              value={form.issuerWebsite ?? ''}
              onChange={(e) => setField('issuerWebsite', e.target.value || null)}
            />
          </Field>
          <p className="text-xs text-text-tertiary mt-1">
            Die Absender-Briefkopf-Zeile über der Empfänger-Adresse wird automatisch aus Firmenname
            + Straße + PLZ Ort gebildet (DIN-5008-konform).
          </p>
        </Section>

        <Section title="Steuer- und Handelsregisterdaten" collapsible>
          <Two>
            <Field label="Steuernummer">
              <Input
                value={form.taxId ?? ''}
                onChange={(e) => setField('taxId', e.target.value || null)}
                placeholder="123/456/78901"
              />
            </Field>
            <Field label="USt-IdNr">
              <Input
                value={form.vatId ?? ''}
                onChange={(e) => setField('vatId', e.target.value || null)}
                placeholder="DE123456789"
              />
            </Field>
          </Two>
          <Two>
            <Field label="Handelsregister">
              <Input
                value={form.commercialRegister ?? ''}
                onChange={(e) => setField('commercialRegister', e.target.value || null)}
                placeholder="HRB 12345 Köln"
              />
            </Field>
            <Field label="Geschäftsführer">
              <Input
                value={form.managingDirector ?? ''}
                onChange={(e) => setField('managingDirector', e.target.value || null)}
              />
            </Field>
          </Two>
          <Field label="Gerichtsstand">
            <Input
              value={form.court ?? ''}
              onChange={(e) => setField('court', e.target.value || null)}
              placeholder="Amtsgericht Dortmund"
            />
          </Field>
          <Toggle
            label="Kleinunternehmer §19 UStG (keine Umsatzsteuer ausweisen)"
            checked={form.smallBusinessClause}
            onChange={(v) => setField('smallBusinessClause', v)}
          />
        </Section>

        <Section title="Bankverbindung" collapsible>
          <Field label="Bankname">
            <Input
              value={form.bankName ?? ''}
              onChange={(e) => setField('bankName', e.target.value || null)}
            />
          </Field>
          <Two>
            <Field label="IBAN">
              <Input
                value={form.bankIban ?? ''}
                onChange={(e) => setField('bankIban', e.target.value || null)}
                placeholder="DE89 3704 0044 0532 0130 00"
              />
            </Field>
            <Field label="BIC">
              <Input
                value={form.bankBic ?? ''}
                onChange={(e) => setField('bankBic', e.target.value || null)}
                placeholder="COBADEFFXXX"
              />
            </Field>
          </Two>
        </Section>

        <Section title="Standardwerte" collapsible>
          <Two>
            <Field label="Zahlungsziel (Tage)">
              <Input
                type="number"
                min="0"
                value={form.defaultPaymentTermsDays}
                onChange={(e) => setField('defaultPaymentTermsDays', Number(e.target.value) || 0)}
              />
            </Field>
            <Field label="MwSt-Satz % (Default)">
              <Input
                type="number"
                step="0.5"
                value={form.defaultTaxRatePercent}
                onChange={(e) => setField('defaultTaxRatePercent', e.target.value)}
              />
            </Field>
          </Two>
          <Two>
            <Field label="Währung">
              <Input
                value={form.defaultCurrency}
                onChange={(e) => setField('defaultCurrency', e.target.value)}
                placeholder="EUR"
              />
            </Field>
            <Field label="Währungs-Anzeige">
              <Select
                value={form.currencyDisplay}
                onChange={(e) =>
                  setField('currencyDisplay', e.target.value as InvoiceSettings['currencyDisplay'])
                }
              >
                <option value="code">Code (12,34 EUR)</option>
                <option value="symbol">Symbol (12,34 €)</option>
              </Select>
            </Field>
          </Two>
          <Field label="Rechnungsnummer-Format">
            <Input
              value={form.invoiceNumberFormat}
              onChange={(e) => setField('invoiceNumberFormat', e.target.value)}
              placeholder="RE-{YYYY}-{####}"
            />
          </Field>
          <Field label="Datums-Format">
            <Select
              value={form.dateFormat}
              onChange={(e) =>
                setField('dateFormat', e.target.value as InvoiceSettings['dateFormat'])
              }
            >
              <option value="de">DD.MM.YYYY (deutsch)</option>
              <option value="iso">YYYY-MM-DD (ISO)</option>
              <option value="us">MM/DD/YYYY (US)</option>
              <option value="long">5. März 2026 (lang)</option>
            </Select>
          </Field>
          <p className="text-xs text-text-tertiary mt-1">
            Platzhalter: <code>{'{YYYY}'}</code>, <code>{'{YY}'}</code>, <code>{'{MM}'}</code>,{' '}
            <code>{'{DD}'}</code>, <code>{'{####}'}</code> (Anzahl <code>#</code> = Stellen).
          </p>
        </Section>

        <Section title="Anrede & Schluss-Text" collapsible>
          <Field label="Anrede / Einleitung">
            <Textarea
              rows={4}
              value={form.greetingTemplate}
              onChange={(e) => setField('greetingTemplate', e.target.value)}
            />
            <p className="text-xs text-text-tertiary mt-1">
              Platzhalter: <code>{'{customer}'}</code>, <code>{'{invoiceNumber}'}</code>,{' '}
              <code>{'{date}'}</code>.
            </p>
          </Field>
          <Field label="Schluss-Text (vor Signatur)">
            <Textarea
              rows={2}
              value={form.closingTemplate}
              onChange={(e) => setField('closingTemplate', e.target.value)}
            />
          </Field>
          <ImageField
            label="Signatur-Bild (PNG/JPG, max 1 MB)"
            value={form.signatureDataUrl}
            onChange={(v) => setField('signatureDataUrl', v)}
            onUpload={(f) => uploadImage('signatureDataUrl', f)}
          />
        </Section>

        <div className="flex justify-end pt-4 border-t border-border-subtle">
          <Button onClick={save} disabled={mutation.isPending} size="lg">
            <Save className="h-4 w-4" />
            {mutation.isPending ? 'Speichere …' : 'Speichern'}
          </Button>
        </div>
      </div>
    </SettingsLayout>
  )
}

function Section({
  title,
  children,
  collapsible = false,
  defaultOpen = false,
}: {
  title: string
  children: React.ReactNode
  collapsible?: boolean
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  if (!collapsible) {
    return (
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">
          {title}
        </h2>
        <div className="space-y-3 rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4">
          {children}
        </div>
      </section>
    )
  }
  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-bg-overlay/30 transition-colors"
      >
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">
          {title}
        </h2>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-text-tertiary transition-transform duration-150',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div className="space-y-3 px-4 pb-4 pt-2 border-t border-border-subtle">{children}</div>
      )}
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      {children}
    </div>
  )
}

function Two({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-accent h-4 w-4"
      />
      {label}
    </label>
  )
}

function ColorField({
  value,
  onChange,
  allowEmpty = false,
}: {
  value: string
  onChange: (v: string) => void
  allowEmpty?: boolean
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-14 rounded-lg border border-border-default cursor-pointer"
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={allowEmpty ? 'leer = automatisch' : '#d97706'}
        className="flex-1"
      />
      {allowEmpty && value && (
        <Button variant="ghost" size="sm" onClick={() => onChange('')}>
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}

function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const { className, ...rest } = props
  return (
    <textarea
      className={cn(
        'flex w-full rounded-lg border border-border-default bg-bg-overlay/50 px-3.5 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary',
        'transition-[border-color,box-shadow] duration-150',
        'focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20 focus-visible:bg-bg-overlay',
        'hover:border-border-default/80 disabled:cursor-not-allowed disabled:opacity-50',
        'resize-y min-h-[64px]',
        className,
      )}
      {...rest}
    />
  )
}

function ImageField({
  label,
  value,
  onChange,
  onUpload,
}: {
  label: string
  value: string | null
  onChange: (v: string | null) => void
  onUpload: (file: File) => void
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-start gap-3">
        <div className="h-20 w-32 rounded-lg border border-border-default bg-bg-overlay/40 flex items-center justify-center overflow-hidden shrink-0">
          {value ? (
            <img src={value} alt={label} className="max-h-full max-w-full object-contain" />
          ) : (
            <ImageIcon className="h-6 w-6 text-text-tertiary" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) onUpload(f)
              e.target.value = ''
            }}
          />
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => inputRef.current?.click()}>
              <Upload className="h-4 w-4" />
              {value ? 'Ersetzen' : 'Hochladen'}
            </Button>
            {value && (
              <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
                <Trash2 className="h-4 w-4" />
                Entfernen
              </Button>
            )}
          </div>
          <p className="text-xs text-text-tertiary">
            PNG oder JPEG, max 1 MB. Wird als Data-URL gespeichert.
          </p>
        </div>
      </div>
    </div>
  )
}
